import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getClient, updateClient } from "../../../data";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AppLayout } from "../shared/layout";
import {
  CommissionCard,
  CustomDataInput,
  ItemCard,
  SocialMediaInput,
  EmailTransactionsTab,
} from "@/components";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useEffect, useState } from "react";
import SocialMediaIcons from "@/components/social-media-icons";
import { useToast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  User,
  Mail,
  Phone,
  Building2,
  CreditCard,
  MapPin,
  Briefcase,
  Share2,
  Database,
  Receipt,
  BarChart3,
  TrendingUp,
  Percent,
  FileText,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = {
  PENDING: "#f59e0b",
  APPROVED: "#10b981",
  REJECTED: "#ef4444",
  PAID: "#3b82f6",
  UNKNOWN: "#6b7280",
};

export function ClientUpdateScreen() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("details");

  const pageSettings = {
    title: "Client Detail",
    actions: [
      {
        name: "Go To List",
        color: "blue",
        icon: "add-icon",
        onClick: () => {
          navigate("/clients");
        },
      },
    ],
  };

  const { id } = useParams();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const watchIsHaveAgency = watch("isHaveAgency");
  const { data, isLoading, error } = useQuery({
    queryKey: ["client", id],
    queryFn: async () => await getClient(id),
  });

  const mutation = useMutation({
    mutationKey: ["updateClient", id],
    mutationFn: (data) => {
      return updateClient(data.id, data.data);
    },
    onSuccess: (response) => {
      toast({
        title: "Client Updated",
        message: `Client ${response.data.name} has been updated`,
        type: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["client", id] });
    },
  });

  const onSubmit = async (data) => {
    if (data.socialMediaAccounts) {
      var isHaveEmptySocialMediaAccount = false;
      data.socialMediaAccounts.map((socialMediaAccount) => {
        if (
          socialMediaAccount.platform === "" ||
          socialMediaAccount.accountId === ""
        ) {
          isHaveEmptySocialMediaAccount = true;
        }
      });

      if (isHaveEmptySocialMediaAccount) {
        alert("Please fill all social media account fields");
        return;
      }
    }

    if (data.customData) {
      var isHaveEmptyCustomData = false;
      data.customData.map((customData) => {
        if (customData.key === "" || customData.value === "") {
          isHaveEmptyCustomData = true;
        }
      });
      if (isHaveEmptyCustomData) {
        alert("Please fill all custom data fields");
        return;
      }
    }

    mutation.mutate({
      id: id,
      data: {
        email: data.email?.trim(),
        name: data.name?.trim(),
        phoneNumber: data.phoneNumber?.trim(),
        billingName: data.billingName?.trim(),
        bankName: data.bankName?.trim(),
        IBAN: data.IBAN?.trim(),
        SWIFT: data.SWIFT?.trim(),
        customData: data.customData,
        isHaveAgency: data.isHaveAgency,
        manager: data.manager?.trim(),
        socialMediaAccounts: data.socialMediaAccounts,
        agencyId: data.agencyId,
        lucaId: data.lucaId?.trim(),
        lucaAccountName: data.lucaAccountName?.trim(),
        currency: data.currency?.trim(),
        country: data.country?.trim(),
        newTvAccountManager: data.newTvAccountManager?.trim(),
        newtvCommissionPercentage: data.newtvCommissionPercentage ? parseFloat(data.newtvCommissionPercentage) : null,
        isPrepareTheInvoiceIndividually: data.isPrepareTheInvoiceIndividually === true,
      },
    });
  };

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div>Loading...</div>
      </AppLayout>
    );
  }

  // Prepare chart data
  const statusData = data.data.commissionStats?.statusBreakdown
    ? Object.entries(data.data.commissionStats.statusBreakdown).map(
        ([status, count]) => ({
          name: status,
          value: count,
          fill: COLORS[status] || COLORS.UNKNOWN,
        })
      )
    : [];

  return (
    <AppLayout
      title={pageSettings.title + " - " + data.data.name}
      actions={pageSettings.actions}
    >
      <div className="px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="details" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Client Details
            </TabsTrigger>
            <TabsTrigger value="commissions" className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Commissions ({data.data.commissionStats?.totalCommissions || 0})
            </TabsTrigger>
            <TabsTrigger value="commission-details" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Commission Details ({data.data.commissionStats?.totalCommissionDetails || 0})
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email History
            </TabsTrigger>
          </TabsList>

          {/* Client Details Tab */}
          <TabsContent value="details">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Basic Information */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        Basic Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <Label htmlFor="name" className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Name *
                        </Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder="Enter client name"
                          className="mt-1.5"
                          {...register("name", {
                            required: "Name is required",
                          })}
                          defaultValue={data.data.name}
                        />
                        {errors.name && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.name.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="email" className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Email *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="client@example.com"
                          className="mt-1.5"
                          {...register("email", {
                            required: "Email is required",
                            pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                              message: "Invalid email format",
                            },
                          })}
                          defaultValue={data.data.email}
                        />
                        {errors.email && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.email.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label
                          htmlFor="phoneNumber"
                          className="flex items-center gap-2"
                        >
                          <Phone className="h-4 w-4" />
                          Phone Number *
                        </Label>
                        <Input
                          id="phoneNumber"
                          type="text"
                          placeholder="+1 234 567 8900"
                          className="mt-1.5"
                          {...register("phoneNumber", {
                            required: "Phone Number is required",
                          })}
                          defaultValue={data.data.phoneNumber}
                        />
                        {errors.phoneNumber && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.phoneNumber.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label
                          htmlFor="billingName"
                          className="flex items-center gap-2"
                        >
                          <User className="h-4 w-4" />
                          Billing Name *
                        </Label>
                        <Input
                          id="billingName"
                          type="text"
                          placeholder="Billing name"
                          className="mt-1.5"
                          {...register("billingName", {
                            required: "Billing Name is required",
                          })}
                          defaultValue={data.data.billingName}
                        />
                        {errors.billingName && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.billingName.message}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Location & Manager */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <MapPin className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        Location & Management
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <Label htmlFor="country" className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          Country *
                        </Label>
                        <Input
                          id="country"
                          type="text"
                          placeholder="Country"
                          className="mt-1.5"
                          {...register("country", {
                            required: "Country is required",
                          })}
                          defaultValue={data.data.country}
                        />
                        {errors.country && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.country.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="currency" className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Currency *
                        </Label>
                        <Input
                          id="currency"
                          type="text"
                          placeholder="EUR, USD, etc."
                          className="mt-1.5"
                          {...register("currency", {
                            required: "Currency is required",
                          })}
                          defaultValue={data.data.currency}
                        />
                        {errors.currency && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.currency.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label
                          htmlFor="newTvAccountManager"
                          className="flex items-center gap-2"
                        >
                          <User className="h-4 w-4" />
                          New TV Account Manager *
                        </Label>
                        <Input
                          id="newTvAccountManager"
                          type="text"
                          placeholder="Manager name"
                          className="mt-1.5"
                          {...register("newTvAccountManager", {
                            required: "New TV Account Manager is required",
                          })}
                          defaultValue={data.data.newTvAccountManager}
                        />
                        {errors.newTvAccountManager && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.newTvAccountManager.message}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Luca Account */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Briefcase className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        Luca Account
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <Label htmlFor="lucaId" className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4" />
                          Luca ID
                        </Label>
                        <Input
                          id="lucaId"
                          type="text"
                          placeholder="Luca ID"
                          className="mt-1.5 font-mono"
                          {...register("lucaId")}
                          defaultValue={data.data.lucaId}
                        />
                        {errors.lucaId && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.lucaId.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label
                          htmlFor="lucaAccountName"
                          className="flex items-center gap-2"
                        >
                          <User className="h-4 w-4" />
                          Luca Account Name
                        </Label>
                        <Input
                          id="lucaAccountName"
                          type="text"
                          placeholder="Luca Account Name"
                          className="mt-1.5"
                          {...register("lucaAccountName")}
                          defaultValue={data.data.lucaAccountName}
                        />
                        {errors.lucaAccountName && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.lucaAccountName.message}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Commission Settings */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Percent className="h-5 w-5 text-green-600 dark:text-green-400" />
                        Commission Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <Label
                          htmlFor="newtvCommissionPercentage"
                          className="flex items-center gap-2"
                        >
                          <Percent className="h-4 w-4" />
                          NewTV Commission Percentage (%)
                        </Label>
                        <Input
                          id="newtvCommissionPercentage"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="e.g. 15"
                          className="mt-1.5"
                          {...register("newtvCommissionPercentage")}
                          defaultValue={data.data.newtvCommissionPercentage}
                        />
                        {errors.newtvCommissionPercentage && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.newtvCommissionPercentage.message}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="isPrepareTheInvoiceIndividually"
                          name="isPrepareTheInvoiceIndividually"
                          onCheckedChange={(checked) => {
                            setValue("isPrepareTheInvoiceIndividually", checked);
                          }}
                          defaultChecked={data.data.isPrepareTheInvoiceIndividually || false}
                        />
                        <Label
                          htmlFor="isPrepareTheInvoiceIndividually"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                        >
                          <FileText className="h-4 w-4" />
                          Prepare Invoice Individually (Bireysel Fatura)
                        </Label>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Agency Information */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Building2 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        Agency Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="isHaveAgency"
                          name="isHaveAgency"
                          onCheckedChange={(value) => {
                            if (!value) {
                              setValue("agencyId", "");
                              data.data.agencyId = "";
                            }
                            setValue("isHaveAgency", value);
                          }}
                          defaultChecked={data.data.agencyId ? true : false}
                        />
                        <Label
                          htmlFor="isHaveAgency"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Client has an agency
                        </Label>
                      </div>

                      {(watchIsHaveAgency || data.data.agencyId) && (
                        <div>
                          <Label>Select Agency</Label>
                          <Select
                            onValueChange={(value) => setValue("agencyId", value)}
                            {...(data.data.agencyId && {
                              defaultValue: data.data.agencyId.toString(),
                            })}
                            {...register("agencyId")}
                          >
                            <SelectTrigger className="mt-1.5">
                              <SelectValue placeholder="Select an agency" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectLabel>Agencies</SelectLabel>
                                {data.data.agencies.map((agency) => (
                                  <SelectItem
                                    value={agency.id.toString()}
                                    key={agency.id}
                                  >
                                    {agency.name}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Banking Details */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <CreditCard className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                        Banking Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <Label htmlFor="bankName" className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          Bank Name *
                        </Label>
                        <Input
                          id="bankName"
                          type="text"
                          placeholder="Bank name"
                          className="mt-1.5"
                          {...register("bankName", {
                            required: "Bank Name is required",
                          })}
                          defaultValue={data.data.bankName}
                        />
                        {errors.bankName && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.bankName.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="IBAN" className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          IBAN *
                        </Label>
                        <Input
                          id="IBAN"
                          type="text"
                          placeholder="IBAN"
                          className="mt-1.5 font-mono"
                          {...register("IBAN", {
                            required: "IBAN is required",
                          })}
                          defaultValue={data.data.IBAN}
                        />
                        {errors.IBAN && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.IBAN.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="SWIFT" className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          SWIFT *
                        </Label>
                        <Input
                          id="SWIFT"
                          type="text"
                          placeholder="SWIFT/BIC code"
                          className="mt-1.5 font-mono"
                          {...register("SWIFT", {
                            required: "SWIFT is required",
                          })}
                          defaultValue={data.data.SWIFT}
                        />
                        {errors.SWIFT && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.SWIFT.message}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Social Media */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/30 dark:to-rose-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Share2 className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                        Social Media Accounts
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <Label className="text-sm text-muted-foreground mb-2 block">
                          Current Social Media
                        </Label>
                        <SocialMediaIcons data={data.data.socialMediaAccounts} />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground mb-2 block">
                          Update Social Media Accounts
                        </Label>
                        <SocialMediaInput
                          value={data.data.socialMediaAccounts}
                          onDataChanged={(data) => {
                            setValue("socialMediaAccounts", data);
                          }}
                        />
                      </div>
                      {errors.socialMediaAccounts && (
                        <p className="text-sm text-destructive mt-2">
                          {errors.socialMediaAccounts.message}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Custom Data */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Database className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                        Custom Data
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <CustomDataInput
                        value={data.data.customData}
                        onDataChanged={(data) => {
                          setValue("customData", data);
                        }}
                      />
                      {errors.customData && (
                        <p className="text-sm text-destructive mt-2">
                          {errors.customData.message}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-8 flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/clients")}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  Update Client
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions">
            <div className="space-y-6">
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          Total Commissions
                        </p>
                        <p className="text-3xl font-bold text-foreground mt-1">
                          {data.data.commissionStats?.totalCommissions || 0}
                        </p>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <Receipt className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          Commission Details
                        </p>
                        <p className="text-3xl font-bold text-foreground mt-1">
                          {data.data.commissionStats?.totalCommissionDetails || 0}
                        </p>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <BarChart3 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-emerald-500">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          Status Types
                        </p>
                        <p className="text-3xl font-bold text-foreground mt-1">
                          {Object.keys(
                            data.data.commissionStats?.statusBreakdown || {}
                          ).length}
                        </p>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                        <TrendingUp className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              {statusData.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pie Chart */}
                  <Card>
                    <CardHeader className="border-b">
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Status Distribution
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={statusData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) =>
                              `${name}: ${(percent * 100).toFixed(0)}%`
                            }
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {statusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Bar Chart */}
                  <Card>
                    <CardHeader className="border-b">
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Status Count
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={statusData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="value" fill="#3b82f6" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Commissions List */}
              {data.data.commissions.length > 0 ? (
                <Card>
                  <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Receipt className="h-5 w-5 text-green-600 dark:text-green-400" />
                      All Commissions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="grid gap-4">
                      {data.data.commissions.map((commission) => (
                        <CommissionCard
                          key={commission.id}
                          isHorizontal={true}
                          influencerName={commission.influencerName}
                          email={commission.email}
                          invoiceId={commission.invoiceId}
                          finalAmount={commission.finalAmount}
                          totalEarnings={commission.totalEarnings}
                          payment={commission.payment}
                          onDetailClick={() => {
                            if (commission.paymentId) {
                              navigate(
                                `/payments/${commission.paymentId}/commissions/${commission.id}`
                              );
                            } else {
                              navigate(`/commissions/${commission.id}`);
                            }
                          }}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium text-muted-foreground">
                      No commissions found for this client
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Commission Details Tab */}
          <TabsContent value="commission-details">
            <div className="space-y-6">
              {/* Commission Details List */}
              {data.data.commissionDetails && data.data.commissionDetails.length > 0 ? (
                <Card>
                  <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      All Commission Details ({data.data.commissionDetails.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-3 font-semibold">Influencer</th>
                            <th className="text-left p-3 font-semibold">Invoice ID</th>
                            <th className="text-left p-3 font-semibold">Invoice Date</th>
                            <th className="text-right p-3 font-semibold">Amount</th>
                            <th className="text-center p-3 font-semibold">Status</th>
                            <th className="text-left p-3 font-semibold">Created At</th>
                            <th className="text-center p-3 font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.data.commissionDetails.map((detail) => (
                            <tr key={detail.id} className="border-b hover:bg-muted/30 transition-colors">
                              <td className="p-3">
                                <div>
                                  <p className="font-medium">{detail.commission?.influencerName || "-"}</p>
                                  <p className="text-xs text-muted-foreground">{detail.commission?.email || "-"}</p>
                                </div>
                              </td>
                              <td className="p-3 font-mono text-xs">
                                {detail.invoiceId || "-"}
                              </td>
                              <td className="p-3">
                                {detail.invoiceDate ? new Date(detail.invoiceDate).toLocaleDateString("tr-TR") : "-"}
                              </td>
                              <td className="p-3 text-right font-semibold text-emerald-600">
                                ${detail.totalAmount?.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}
                              </td>
                              <td className="p-3 text-center">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  detail.status === "Invoice Uploaded" ? "bg-yellow-100 text-yellow-800" :
                                  detail.status === "Approved" ? "bg-blue-100 text-blue-800" :
                                  detail.status === "Accounting" ? "bg-purple-100 text-purple-800" :
                                  detail.status === "LUCA" ? "bg-orange-100 text-orange-800" :
                                  detail.status === "Paid" ? "bg-green-100 text-green-800" :
                                  detail.status === "Change Requested" ? "bg-red-100 text-red-800" :
                                  "bg-muted text-muted-foreground"
                                }`}>
                                  {detail.status || "Unknown"}
                                </span>
                              </td>
                              <td className="p-3 text-xs text-muted-foreground">
                                {detail.createdAt ? new Date(detail.createdAt).toLocaleDateString("tr-TR") : "-"}
                              </td>
                              <td className="p-3 text-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    if (detail.commission?.paymentId) {
                                      navigate(`/payments/${detail.commission.paymentId}/invoices/${detail.id}`);
                                    }
                                  }}
                                  disabled={!detail.commission?.paymentId}
                                >
                                  View
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium text-muted-foreground">
                      No commission details found for this client
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Commission details will appear here when invoices are uploaded.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Email History Tab */}
          <TabsContent value="emails">
            <EmailTransactionsTab clientId={parseInt(id)} type="client" />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
